import { TCountryCode } from 'countries-list';
import { AnswerValue, ProfilerQuestionOption } from 'types/';

export interface CommitmentInitiatePayload {
  commitment_option_id: string;
}

export interface CommitmentNextActionPayload {
  id: string;
}

export interface CommitmentResultPayload {
  url: string;
}

export interface ProfilerQuestionPayload {
  id?: string;
}

export interface CommitmentAnswerPayload {
  id: string;
  profiler_id: number;
  localization_id: number;
  time_to_answer: number;
  profiler_options?: ProfilerQuestionOption[];
  answer: AnswerValue;
  other?: any;
  verisoul_session_id?: string;
  input_data?: any; // Keyboard and input event logging data for fraud detection
}

export interface CommitmentFeedbackPayload {
  id: string;
  profiler_id: number;
  localization_id: number;
  time_to_answer: number;
  profiler_options?: ProfilerQuestionOption[];
  answer: AnswerValue;
  other?: any;
  verisoul_session_id?: string;
}

export interface CommitmentVerisoulFaceCheckPayload {
  session_id: string;
  status: string;
}

export interface CommitmentVerisoulFaceCheckResponse {
  success: boolean;
  redirect_url: string;
}

export interface SupplierLogInPayload {
  url: string;
  verisoul_session_id?: string;
}

export interface SupplierSignUpPayload extends SupplierLogInPayload {
  agree_termsofservice: boolean;
  agree_privacypolicy: boolean;
  agree_cookiepolicy: boolean;
}

export enum SupplierStatsDateGrouping {
  // TODO: uncomment other values when BE will be ready
  // Second = 'second',
  // Minute = 'minute',
  Hour = 'hour',
  Day = 'day',
  Week = 'week',
  Month = 'month',
  // Quarter = 'quarter',
  // Year = 'year',
}

export interface SupplierStatsQueryParams {
  /**
   * @format ISO 8601 date string in UTC timezone
   * @example 2021-09-01T00:00:00
   */
  from?: string;
  /**
   * @format ISO 8601 date string in UTC timezone
   * @example 2021-09-30T23:59:59
   */
  until?: string;
  date_grouping?: SupplierStatsDateGrouping;
  country_grouping?: boolean;
  country_codes?: TCountryCode[];
  page: number;
  page_size: number;
}

export interface SupplierTransactionsQueryParams {
  /**
   * @format ISO 8601 date string in UTC timezone
   * @example 2021-09-01T00:00:00
   */
  from?: string;
  /**
   * @format ISO 8601 date string in UTC timezone
   * @example 2021-09-30T23:59:59
   */
  until?: string;
  country_codes?: TCountryCode[];
  mid?: string;
  pid?: string;
  outcome?: number[];
  page: number;
  page_size: number;
}

export interface SupplierStatsResultsPayload {
  date_group: string;
  supplier_id: string | number;
  entries: number;
  completes: number;
  conversion: number;
  throttled: number;
  terminated: number;
  blacklisted: number;
  earnings: number;
  reversals: number;
  epc: number;
  cpi: number;
  loi: number | string;
  drops: number;
  median_commitment_duration: string | null;
  median_commitment_duration2: string | null;
  mean_commitment_duration: string | number;
  mean_loi: string | number;
  loi2: string | number;
}

export interface SupplierStatsPayload {
  pagination: {
    current: number;
    has_next: boolean;
    has_previous: boolean;
    count: number;
    num_pages: number;
    page_size: number;
  };
  result: SupplierStatsResultsPayload[];
}

export interface SupplierTransactionsResultsPayload {
  id: number;
  datetime_started: string;
  datetime_updated: string;
  pid: string;
  mid: string;
  cpi: number;
  outcome_label: string | null;
  country: string;
}

export interface SupplierTransactionsPayload {
  pagination: {
    current: number;
    has_next: boolean;
    has_previous: boolean;
    count: number;
    num_pages: number;
    page_size: number;
  };
  result: SupplierTransactionsResultsPayload[];
}

export interface SupplierUrlsPayload {
  redirects?: {
    complete?: string;
    term?: string;
    oq?: string;
    blacklist?: string;
  };
  s2s?: {
    complete?: string;
    reversal?: string;
    term?: string;
    oq?: string;
    blacklist?: string;
    method?: string;
  };
}

export interface SupplierUrlsParams {
  redirects: {
    complete: string;
    term: string;
    oq: string;
    blacklist: string;
  };
  s2s: {
    complete: string;
    reversal: string;
    method: string;
  };
}

export interface SupplierIframeSettingsPayload {
  payout_currency: string;
  payout_multiplier: string;
}

export interface SkipAnswerAndNextActionPayload {
  profiler_id: number;
  report_reason?: string;
}

export interface spinAndNextActionPayload {
  rx_flow_decision: string;
}

export interface pauseProfilerDecisionAndNextActionPayload {
  rx_flow_decision: string;
}

export interface abandonStudyAndNextActionPayload {
  reason: string;
  detail: string;
}

export interface PostbackTestResponse {
  url: string;
  status: string | number;
}

// Single event in viewing history
export interface VideoViewingEvent {
  timestamp: number; // ms since component mounted
  event: string; // e.g. "play", "pause", "seek", "ended"
  data?: Record<string, unknown>; // Optional payload for event-specific data (e.g. { to: 30 } for seek)
}

// Payload for reporting video watch completion
export interface ReportVideoWatchedPayload {
  video_name: string;
  video_version: string | null; // null if ?v= query param not present
  video_length: number; // video duration in seconds
  time_started: number; // ms since component mounted when video first played
  time_viewed: number; // total ms watched
  muted: boolean;
  viewing_history: {
    events: VideoViewingEvent[];
  };
}
